import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Building2, Eye, Factory, FileText, Layers3, MapPin, Plus, Trash2, Wrench, X } from "lucide-react";
import { PROJECT_PATH_FIELDS, projectNodePaths } from "lib/utils/projectNodePaths";
import "./UploadWorkspace.css";

const FIELDS = [
  ["facilityType", "설비 구분", "facilityTypes"],
  ["buildingName", "건물명", "buildingNames"],
  ["floor", "층", "floors"],
  ["location", "위치", "locations"],
  ["facilityName", "설비명", "facilityNames"],
  ["partName", "부품명", "partNames"],
];
const emptyGroups = () => Array(6).fill(null);
const pathKey = groups => JSON.stringify(groups.slice(1).map(value => String(value ?? "").trim()));
const pathGroups = path => [null, ...PROJECT_PATH_FIELDS.map(({ key }) => path[key] || null)];
const TREE_ICONS = [null, Building2, Layers3, MapPin, Factory, Wrench];

export default function UploadWorkspace({ uploadedFiles, setUploadedFiles, setThumbnails, thumbnails = [], nodes = [], metadataOptions = {}, onSelectTree, actions, filePicker }) {
  const entries = useMemo(() => Object.entries(uploadedFiles ?? {}), [uploadedFiles]);
  const keys = useMemo(() => entries.map(([key]) => key), [entries]);
  const [selected, setSelected] = useState([]);
  const [target, setTarget] = useState("");
  const [previewKey, setPreviewKey] = useState(null);
  const [hoveredFile, setHoveredFile] = useState(null);
  const [hoverPreview, setHoverPreview] = useState(null);
  const [bulk, setBulk] = useState(() => emptyGroups());
  const [dragOver, setDragOver] = useState("");
  const [unclassifiedDragOver, setUnclassifiedDragOver] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [draftPaths, setDraftPaths] = useState([]);
  const [creating, setCreating] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [draftError, setDraftError] = useState("");
  const dragKeys = useRef([]);
  const lastSelected = useRef(null);
  const popupRef = useRef(null);
  const fileKeysRef = useRef(keys);
  const listId = useId().replace(/:/g, "");
  const thumbnailsByKey = useMemo(() => new Map(thumbnails.map(item => [String(item.name), item])), [thumbnails]);

  useEffect(() => { fileKeysRef.current = keys; }, [keys]);

  useEffect(() => {
    setSelected(current => {
      const existing = current.filter(key => keys.includes(key));
      return existing.length === current.length ? current : existing;
    });
    if (previewKey && !keys.includes(previewKey)) setPreviewKey(null);
  }, [keys, previewKey]);

  useEffect(() => {
    if (!previewKey) return;
    const onKeyDown = event => {
      if (event.key === "Escape") setPreviewKey(null);
      if (event.target instanceof HTMLInputElement) return;
      const currentKeys = fileKeysRef.current;
      const index = currentKeys.indexOf(previewKey);
      if (event.key === "ArrowRight" && index < currentKeys.length - 1) setPreviewKey(currentKeys[index + 1]);
      if (event.key === "ArrowLeft" && index > 0) setPreviewKey(currentKeys[index - 1]);
    };
    window.addEventListener("keydown", onKeyDown);
    popupRef.current?.focus();
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [previewKey]);

  const tree = useMemo(() => {
    const rows = new Map();
    const add = groups => {
      let parentKey = null;
      for (let depth = 1; depth <= 5; depth += 1) {
        if (!groups[depth]) continue;
        const prefix = [null, ...groups.slice(1, depth + 1), ...Array(5 - depth).fill(null)];
        const key = pathKey(prefix);
        const previous = rows.get(key);
        rows.set(key, {
          key, groups: prefix, depth, name: groups[depth], parentKey,
          id: previous?.id ?? null,
          count: previous?.count ?? 0,
          draft: previous?.draft ?? true,
        });
        parentKey = key;
      }
    };
    projectNodePaths(nodes).forEach(path => {
      const groups = pathGroups(path);
      add(groups);
      const key = pathKey(groups);
      const row = rows.get(key);
      if (row) rows.set(key, { ...row, id: path.id, draft: false });
    });
    draftPaths.forEach(add);
    entries.forEach(([, data]) => {
      const groups = data.groups ?? emptyGroups();
      add(groups);
      const row = rows.get(pathKey(groups));
      if (row) row.count += 1;
    });
    const children = new Map();
    rows.forEach(row => {
      const siblings = children.get(row.parentKey) ?? [];
      siblings.push(row);
      children.set(row.parentKey, siblings);
    });
    const ordered = [];
    const visit = parentKey => {
      (children.get(parentKey) ?? [])
        .sort((a, b) => a.name.localeCompare(b.name, "ko", { numeric: true }))
        .forEach(row => { ordered.push({ ...row, hasChildren: children.has(row.key) }); visit(row.key); });
    };
    visit(null);
    return ordered;
  }, [nodes, entries, draftPaths]);
  const treeByKey = useMemo(() => new Map(tree.map(row => [row.key, row])), [tree]);
  const filesByPath = useMemo(() => {
    const result = new Map();
    entries.forEach(([key, data]) => {
      const path = pathKey(data.groups ?? emptyGroups());
      const files = result.get(path) ?? [];
      files.push(key);
      result.set(path, files);
    });
    return result;
  }, [entries]);
  const unclassifiedEntries = useMemo(() => entries.filter(([, data]) =>
    !(data.groups ?? emptyGroups()).slice(1).some(value => String(value ?? "").trim()),
  ), [entries]);
  const unclassifiedKeys = useMemo(() => unclassifiedEntries.map(([key]) => key), [unclassifiedEntries]);
  const hoverThumbnail = hoveredFile && thumbnailsByKey.get(String(hoveredFile.key));
  const hoverFileVisible = hoveredFile && unclassifiedKeys.includes(hoveredFile.key);

  useEffect(() => {
    setHoverPreview(null);
    if (!hoveredFile || !hoverFileVisible || !hoverThumbnail?.url || previewKey) return;
    const dismiss = () => { setHoveredFile(null); setHoverPreview(null); };
    const onKeyDown = event => { if (event.key === "Escape") dismiss(); };
    const timer = window.setTimeout(() => {
      if (!hoveredFile.element.isConnected || !hoveredFile.element.matches(":hover")) return;
      const rect = hoveredFile.element.getBoundingClientRect();
      const width = Math.min(rect.width, window.innerWidth - 16);
      const height = Math.min(292.5, window.innerHeight - 16);
      setHoverPreview({
        key: hoveredFile.key, url: hoverThumbnail.url, width, height,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        top: rect.top >= height + 16 ? rect.top - height - 8
          : Math.max(8, Math.min(rect.bottom + 8, window.innerHeight - height - 8)),
      });
    }, 500);
    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    window.addEventListener("blur", dismiss);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
      window.removeEventListener("blur", dismiss);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [hoveredFile, hoverFileVisible, hoverThumbnail?.url, previewKey]);

  const dismissHoverPreview = () => { setHoveredFile(null); setHoverPreview(null); };
  const removeFile = key => {
    dismissHoverPreview();
    setUploadedFiles(previous => { const next = { ...previous }; delete next[key]; return next; });
    setThumbnails?.(previous => previous.filter(item => String(item.name) !== String(key)));
    setSelected(current => current.filter(item => item !== key));
    dragKeys.current = dragKeys.current.filter(item => item !== key);
    if (lastSelected.current === key) lastSelected.current = null;
    if (previewKey === key) setPreviewKey(null);
  };
  const isVisible = row => {
    let parentKey = row.parentKey;
    while (parentKey) {
      if (collapsed.has(parentKey)) return false;
      parentKey = treeByKey.get(parentKey)?.parentKey;
    }
    return true;
  };
  const targetRow = treeByKey.get(target);
  const toggleCollapsed = row => {
    if (!collapsed.has(row.key) && targetRow && targetRow.key !== row.key) {
      let parentKey = targetRow.parentKey;
      while (parentKey) {
        if (parentKey === row.key) {
          setTarget(row.key);
          onSelectTree?.(row.groups);
          break;
        }
        parentKey = treeByKey.get(parentKey)?.parentKey;
      }
    }
    setCollapsed(current => {
      const next = new Set(current);
      if (next.has(row.key)) next.delete(row.key);
      else next.add(row.key);
      return next;
    });
  };
  const previewIndex = keys.indexOf(previewKey);
  const previewData = uploadedFiles?.[previewKey];
  const previewThumb = thumbnailsByKey.get(String(previewKey));

  const startCreating = parent => {
    setCreating({ parentKey: parent?.key ?? null, depth: parent ? parent.depth + 1 : 1, groups: parent?.groups ?? emptyGroups() });
    setDraftName("");
    setDraftError("");
    if (parent) setCollapsed(current => { const next = new Set(current); next.delete(parent.key); return next; });
  };
  const finishCreating = () => {
    if (!creating) return;
    const name = draftName.trim();
    const limit = creating.depth === 5 ? 255 : 100;
    if (!name || name.length > limit || name === "__unassigned__") {
      setDraftError(`이름을 입력하세요. ${limit}자 이하로 작성할 수 있습니다.`);
      return;
    }
    const groups = [...creating.groups];
    groups[creating.depth] = name;
    const key = pathKey(groups);
    if (treeByKey.has(key)) {
      setDraftError("같은 경로가 이미 있습니다. 기존 항목을 선택하세요.");
      return;
    }
    setDraftPaths(current => [...current, groups]);
    setTarget(key);
    onSelectTree?.(groups);
    setCreating(null);
    setDraftName("");
    setDraftError("");
  };
  const renderDraftEditor = depth => <div className="uploadWorkspace__draftEditor" style={{ "--tree-depth": depth - 1 }}>
    <input autoFocus type="text" value={draftName} maxLength={depth === 5 ? 255 : 100}
      aria-label={`새 ${PROJECT_PATH_FIELDS[depth - 1].label}`} placeholder={`새 ${PROJECT_PATH_FIELDS[depth - 1].label}`}
      onChange={event => { setDraftName(event.target.value); setDraftError(""); }}
      onKeyDown={event => { if (event.key === "Enter") finishCreating(); if (event.key === "Escape") setCreating(null); }} />
    <button type="button" onClick={finishCreating}>추가</button>
    <button type="button" aria-label="추가 취소" onClick={() => setCreating(null)}><X size={15} aria-hidden="true" /></button>
    {draftError && <span role="alert">{draftError}</span>}
  </div>;

  const updateGroups = (fileKeys, change) => setUploadedFiles(previous => {
    const updated = { ...previous };
    fileKeys.forEach(key => {
      const file = updated[key];
      if (!file) return;
      const groups = [...(file.groups ?? emptyGroups())];
      const previousPath = pathKey(groups);
      change(groups, file);
      const pathChanged = previousPath !== pathKey(groups);
      updated[key] = { ...file, groups, node_id: pathChanged ? null : file.node_id, qr_id: pathChanged ? null : file.qr_id, defaultApplied: Array(6).fill(false) };
    });
    return updated;
  });
  const assign = (fileKeys, row) => {
    if (!row || !fileKeys.length) return;
    setUploadedFiles(previous => {
      const updated = { ...previous };
      fileKeys.forEach(key => {
        const file = updated[key];
        if (!file) return;
        updated[key] = {
          ...file, groups: [file.groups?.[0] ?? null, ...row.groups.slice(1)],
          node_id: row.id || null, qr_id: null, defaultApplied: Array(6).fill(false),
        };
      });
      return updated;
    });
  };
  const toggle = (key, checked) => setSelected(current => checked ? [...new Set([...current, key])] : current.filter(item => item !== key));
  const selectRow = (event, key) => {
    if (event.ctrlKey || event.metaKey) toggle(key, !selected.includes(key));
    else if (event.shiftKey && lastSelected.current && keys.includes(lastSelected.current)) {
      const start = keys.indexOf(lastSelected.current);
      const end = keys.indexOf(key);
      setSelected(keys.slice(Math.min(start, end), Math.max(start, end) + 1));
    } else setSelected(current => current.includes(key) ? current.filter(item => item !== key) : [key]);
    lastSelected.current = key;
  };
  const applyBulk = emptyOnly => {
    updateGroups(selected, groups => bulk.forEach((value, index) => {
      if (String(value ?? "").trim() && (!emptyOnly || !String(groups[index] ?? "").trim())) groups[index] = value.trim();
    }));
    setSelected([]);
    lastSelected.current = null;
  };
  const renderFields = (groups, onChange) => FIELDS.map(([key, label], index) => (
    <label className="uploadWorkspace__field" key={key}>
      <span>{label}</span>
      <input type="text" list={`${listId}-${key}`} value={groups?.[index] ?? ""} maxLength={255}
        onChange={event => onChange(index, event.target.value)} placeholder={`${label} 입력 또는 선택`} />
    </label>
  ));

  return <div className="uploadWorkspace">
    <div className="uploadWorkspace__heading">
      <div><h3>파일 업로드 <span className="uploadWorkspace__total">대기 {keys.length}개 · 배치 {keys.length - unclassifiedKeys.length}개 · 미분류 {unclassifiedKeys.length}개</span></h3>
      </div>
      <div className="uploadWorkspace__actions">{actions}</div>
    </div>
    <details className="uploadWorkspace__bulk"><summary>선택 파일 일괄 적용 <span>{selected.length}개 선택</span></summary>
      <p>값을 입력한 항목만 체크한 파일에 적용합니다.</p>
      <div className="uploadWorkspace__fields">{renderFields(bulk, (index, value) => setBulk(current => current.map((item, i) => i === index ? value : item)))}</div>
      <div className="uploadWorkspace__bulkActions"><button type="button" disabled={!selected.length || !bulk.some(value => String(value ?? "").trim())} onClick={() => applyBulk(true)}>빈 항목만 채우기</button>
        <button type="button" disabled={!selected.length || !bulk.some(value => String(value ?? "").trim())} onClick={() => applyBulk(false)}>선택 파일에 적용</button></div>
    </details>
    <div className="uploadWorkspace__columns">
      <section className="uploadWorkspace__pane uploadWorkspace__treePane" aria-label="현장 경로">
        <div className="uploadWorkspace__paneHead"><h4>현장 경로</h4><div className="uploadWorkspace__paneTools"><span>{tree.length}개 항목</span>
          <button type="button" onClick={() => startCreating(null)}><Plus size={14} aria-hidden="true" /> 건물 추가</button></div></div>
        <div className="uploadWorkspace__tree" role="tree" aria-label="파일을 배치할 경로">
          {creating?.parentKey === null && renderDraftEditor(1)}
          {tree.length ? tree.filter(isVisible).map(row => {
            const Icon = TREE_ICONS[row.depth];
            const assignedFiles = filesByPath.get(row.key) ?? [];
            const expandable = row.hasChildren || assignedFiles.length > 0;
            const isExpanded = !collapsed.has(row.key);
            return <div key={row.key}>
              <div role="treeitem" aria-level={row.depth} aria-selected={target === row.key}
                aria-expanded={expandable ? isExpanded : undefined}
                className={`uploadWorkspace__treeRow${target === row.key ? " is-active" : ""}${dragOver === row.key ? " is-dragOver" : ""}`}
                style={{ "--tree-depth": row.depth - 1 }}
                onDragOver={event => { event.preventDefault(); setDragOver(row.key); }} onDragLeave={() => setDragOver("")}
                onDrop={event => { event.preventDefault(); assign(dragKeys.current, row); dragKeys.current = []; setDragOver(""); }}>
                {expandable ? <button type="button" className="uploadWorkspace__treeToggle"
                  aria-label={`${row.name} 하위 항목 ${isExpanded ? "접기" : "펼치기"}`}
                  aria-expanded={isExpanded} onClick={() => toggleCollapsed(row)}>
                  <span aria-hidden="true">{isExpanded ? "▾" : "▸"}</span>
                </button> : <span className="uploadWorkspace__treeTogglePlaceholder" aria-hidden="true" />}
                <button type="button" className="uploadWorkspace__treeSelect"
                  aria-label={`${PROJECT_PATH_FIELDS[row.depth - 1].label} ${row.name} 선택`}
                  onClick={() => { setTarget(row.key); onSelectTree?.(row.groups); }}>
                  <Icon size={16} strokeWidth={1.8} className="uploadWorkspace__nodeIcon" aria-hidden="true" />
                  <span className="uploadWorkspace__treeName">{row.name}</span>
                  {row.draft && <span className="uploadWorkspace__draft">임시</span>}
                  {row.count > 0 && <span className="uploadWorkspace__count">대기 {row.count}</span>}
                </button>
                {row.depth < 5 && <button type="button" className="uploadWorkspace__addChild"
                  aria-label={`${row.name} 아래 ${PROJECT_PATH_FIELDS[row.depth].label} 추가`}
                  title={`${PROJECT_PATH_FIELDS[row.depth].label} 추가`} onClick={() => startCreating(row)}>
                  <Plus size={15} aria-hidden="true" />
                </button>}
                {row.draft && !row.hasChildren && !row.count && draftPaths.some(groups => pathKey(groups) === row.key) &&
                  <button type="button" className="uploadWorkspace__removeDraft" aria-label={`${row.name} 임시 항목 제거`}
                    onClick={() => { setDraftPaths(current => current.filter(groups => pathKey(groups) !== row.key)); if (target === row.key) setTarget(""); }}>
                    <X size={14} aria-hidden="true" />
                  </button>}
              </div>
              {creating?.parentKey === row.key && renderDraftEditor(creating.depth)}
              {isExpanded && assignedFiles.map(key => <div key={key} role="treeitem" aria-level={row.depth + 1} aria-selected={selected.includes(key)}
                className={`uploadWorkspace__treeFile is-pending${selected.includes(key) ? " is-selected" : ""}`}
                style={{ "--tree-depth": row.depth }} draggable
                onDragStart={event => { dragKeys.current = selected.includes(key) ? selected : [key]; event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData("text/plain", dragKeys.current.join("\n")); }}
                onDragEnd={() => { dragKeys.current = []; }}>
                <FileText size={15} strokeWidth={1.8} aria-hidden="true" />
                <button type="button" className="uploadWorkspace__treeFileName" title={key}
                  onClick={event => selectRow(event, key)}>{key}</button>
                <span className="uploadWorkspace__treeFileStatus">저장 대기</span>
                <button type="button" className="uploadWorkspace__treeFilePreview" aria-label={`${key} 미리보기와 정보 편집`}
                  onClick={() => setPreviewKey(key)}><Eye size={15} aria-hidden="true" /></button>
              </div>)}
            </div>;
          }) : <p className="uploadWorkspace__empty">등록된 경로가 없습니다. 건물을 추가하거나 파일 미리보기에서 경로를 입력하세요.</p>}
        </div>
      </section>
      <div className="uploadWorkspace__transfer" aria-label="경로 배치">
        <button type="button" disabled={!selected.length || !targetRow} onClick={() => assign(selected, targetRow)} title="선택한 경로에 파일 배치" aria-label="선택한 경로에 파일 배치">←</button>
        <button type="button" disabled={!selected.length} onClick={() => updateGroups(selected, groups => { for (let i = 1; i < 6; i += 1) groups[i] = null; })} title="선택 파일의 경로 배치 해제" aria-label="선택 파일의 경로 배치 해제">→</button>
      </div>
      <section className={`uploadWorkspace__pane uploadWorkspace__filesPane${unclassifiedDragOver ? " is-dragOver" : ""}`} aria-label="미분류 파일"
        onDragOver={event => { if (dragKeys.current.length) { event.preventDefault(); setUnclassifiedDragOver(true); } }}
        onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget)) setUnclassifiedDragOver(false); }}
        onDrop={event => { event.preventDefault(); updateGroups(dragKeys.current, groups => { for (let i = 1; i < 6; i += 1) groups[i] = null; }); dragKeys.current = []; setUnclassifiedDragOver(false); }}>
        <div className="uploadWorkspace__paneHead"><h4>미분류 파일</h4><span>{unclassifiedKeys.length}개</span></div>
        {filePicker}
        <label className="uploadWorkspace__selectAll"><input type="checkbox" checked={unclassifiedKeys.length > 0 && unclassifiedKeys.every(key => selected.includes(key))}
          onChange={event => setSelected(current => event.target.checked
            ? [...new Set([...current, ...unclassifiedKeys])]
            : current.filter(key => !unclassifiedKeys.includes(key)))} /> 전체 선택 <span>{unclassifiedKeys.filter(key => selected.includes(key)).length}개 선택</span></label>
        <div className="uploadWorkspace__files">
          {unclassifiedEntries.map(([key]) => <div key={key} className={`uploadWorkspace__file${selected.includes(key) ? " is-selected" : ""}`}
            onMouseEnter={event => { if (!event.buttons) setHoveredFile({ key, element: event.currentTarget }); }}
            onMouseLeave={dismissHoverPreview}
            onMouseDown={dismissHoverPreview}
            draggable onDragStart={event => { dismissHoverPreview(); dragKeys.current = selected.includes(key) ? selected : [key]; event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData("text/plain", dragKeys.current.join("\n")); }}
            onDragEnd={() => { dragKeys.current = []; }}
            onClick={event => selectRow(event, key)}>
            <input type="checkbox" aria-label={`${key} 선택`} checked={selected.includes(key)} onClick={event => event.stopPropagation()}
              onChange={event => toggle(key, event.target.checked)} />
            <span className="uploadWorkspace__fileName" title={key}>{key}</span>
            <button type="button" className="uploadWorkspace__previewButton" aria-label={`${key} 미리보기와 정보 편집`} title="미리보기와 정보 편집"
              onClick={event => { event.stopPropagation(); setPreviewKey(key); }}><Eye size={17} aria-hidden="true" /></button>
            <button type="button" className="uploadWorkspace__removeFileButton" aria-label={`${key} 대기 목록에서 제거`} title="대기 목록에서 제거"
              onClick={event => { event.stopPropagation(); removeFile(key); }}><Trash2 size={17} aria-hidden="true" /></button>
          </div>)}
          {!unclassifiedKeys.length && <p className="uploadWorkspace__empty">{keys.length ? "모든 파일이 경로에 배치됐습니다." : "파일을 추가하면 여기에 표시됩니다."}</p>}
        </div>
      </section>
    </div>

    {FIELDS.map(([key, , optionsKey]) => <datalist id={`${listId}-${key}`} key={key}>
      {(metadataOptions[optionsKey] ?? []).map(value => <option key={value} value={value} />)}
    </datalist>)}
    {hoverPreview && hoverFileVisible && !previewKey && createPortal(
      <div className="uploadWorkspace__hoverPreview" style={{ left: hoverPreview.left, top: hoverPreview.top, width: hoverPreview.width, height: hoverPreview.height }}>
        <img src={hoverPreview.url} alt={`${hoverPreview.key} 미리보기`} draggable={false} onError={dismissHoverPreview} />
      </div>, document.body,
    )}
    {previewData && <div className="uploadWorkspace__overlay" onMouseDown={event => { if (event.target === event.currentTarget) setPreviewKey(null); }}>
      <div className="uploadWorkspace__modal" role="dialog" aria-modal="true" aria-label="파일 미리보기와 정보 편집" tabIndex={-1} ref={popupRef}>
        <header><div><strong>{previewKey}</strong><span>{previewIndex + 1} / {keys.length}</span></div><button type="button" aria-label="닫기" onClick={() => setPreviewKey(null)}>✕</button></header>
        <div className="uploadWorkspace__modalBody"><div className="uploadWorkspace__media">
          {previewThumb?.url ? <img src={previewThumb.url} alt={`${previewKey} 미리보기`} /> : <p>미리보기를 준비 중입니다.</p>}
        </div><div className="uploadWorkspace__editor"><h4>파일 정보</h4><p>입력한 경로는 왼쪽 목록에 바로 표시됩니다.</p>
          <div className="uploadWorkspace__fields">{renderFields(previewData.groups, (index, value) => updateGroups([previewKey], groups => { groups[index] = value; }))}</div>
          <button type="button" className="uploadWorkspace__remove" onClick={() => removeFile(previewKey)}>대기 목록에서 제거</button>
        </div></div>
        <footer><button type="button" disabled={previewIndex <= 0} onClick={() => setPreviewKey(keys[previewIndex - 1])}>← 이전 파일</button>
          <button type="button" disabled={previewIndex >= keys.length - 1} onClick={() => setPreviewKey(keys[previewIndex + 1])}>다음 파일 →</button></footer>
      </div>
    </div>}
  </div>;
}
